import { createSelector } from "@reduxjs/toolkit";

import { validateAPIError } from "Api/utils";
import { CONTINUOUS_PROFILING_KEY } from "Constants/constants";

import type { RootState } from "Store/configureStore";
import type { ProfilingLevel } from "./types";

const projectAddonsStateSelector = (state: RootState) => state.projectAddons;

export const projectAddonsLoadingSelector = createSelector(
  projectAddonsStateSelector,
  (_: RootState, { projectId }: { projectId: string }) => projectId,
  (projectAddons, projectId) => projectAddons.loadingAddons[projectId] ?? false
);

export const projectAddonsUpdatingSelector = createSelector(
  projectAddonsStateSelector,
  projectAddons => projectAddons.updating
);

export const projectAddonsErrorSelector = createSelector(
  projectAddonsStateSelector,
  (_: RootState, { projectId }: { projectId: string }) => projectId,
  (projectAddons, projectId) => projectAddons.error[projectId]
);

// Normalizes whatever a thunk stored in `error` (raw API error, JSON string,
// or normalized APIError) into a displayable title, so every failure path
// surfaces a message consistently.
export const projectAddonsErrorMessageSelector = createSelector(
  projectAddonsErrorSelector,
  error => validateAPIError(error)?.title
);

export const projectAddonsSelector = createSelector(
  projectAddonsStateSelector,
  (_: RootState, { projectId }: { projectId: string }) => projectId,
  (projectAddons, projectId) => projectAddons.data[projectId]
);

export const projectAddonsByTypeSelector = createSelector(
  projectAddonsSelector,
  addons => {
    if (!addons?.items) return {};
    return addons.items.reduce<
      Record<
        string,
        {
          id: string;
          sku?: string;
          quantity?: number;
          allowed_values?: (number | string)[];
          plan_options?: Record<string, number[]>;
        }
      >
    >((acc, addon) => {
      acc[addon.type] = {
        id: addon.id,
        sku: addon.sku,
        quantity: addon.quantity,
        allowed_values: addon.allowed_values,
        plan_options: addon.plan_options
      };
      return acc;
    }, {});
  }
);

export const profilingCurrentLevelSelector = createSelector(
  projectAddonsSelector,
  (addons): ProfilingLevel =>
    addons?.items?.some(addon => addon.type === CONTINUOUS_PROFILING_KEY)
      ? "advanced"
      : "standard"
);

const profilingSelectionDraftSelector = createSelector(
  projectAddonsStateSelector,
  (_: RootState, { projectId }: { projectId: string }) => projectId,
  (projectAddons, projectId) => projectAddons.profilingSelection[projectId]
);

export const profilingSelectionSelector = createSelector(
  profilingSelectionDraftSelector,
  profilingCurrentLevelSelector,
  (draft, currentLevel): ProfilingLevel => draft ?? currentLevel
);
